import { IconChevronsLeft, IconChevronsRight } from '@tabler/icons-react';
import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useLocation } from 'react-router';
import Navbar from 'components/Navbar/Navbar';
import Sidebar from 'components/Sidebar';
import Footer from 'components/Footer';
import { useLocalStorage } from 'utils/hooks/useStorage';
import LoggedAsBanner from 'components/LoggedAsBanner';
import WelcomeDialog from 'components/WelcomeDialog';
import { useZendeskWidget } from 'utils/hooks/useZendeskWidget';
import usePageTitle from 'utils/hooks/usePageTitle';
import { EnvironmentRibbon } from 'components/EnvironmentRibbon';
import { useSitesCount } from 'utils/hooks/useSitesCount';

// FYI: ref is the tailwind.config
const XXL_SCREEN = 1280;

const Layout = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const [isSmallScreen, setIsSmallScreen] = useState(
        window.innerWidth < XXL_SCREEN
    );

    const totalSitesCount = useSitesCount();
    const isSidebarLoading = totalSitesCount === undefined;
    const isSidebarAvailable = !isSidebarLoading && Number(totalSitesCount) > 1;

    const [storedIsSidebarOpen, setStoredIsSidebarOpen] =
        useLocalStorage<boolean>('isSidebarOpen', !isSmallScreen);

    const isSidebarOpen = isSidebarAvailable && storedIsSidebarOpen;
    const SidebarToggleIcon = isSidebarOpen
        ? IconChevronsLeft
        : IconChevronsRight;

    // Close Sidebar on page change and reload for small screens
    useEffect(() => {
        if (isSidebarAvailable && isSmallScreen) {
            setStoredIsSidebarOpen(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location, isSidebarAvailable, isSmallScreen]);

    useEffect(() => {
        const handleScreenSizeChange = (event: Event) => {
            setIsSmallScreen((event.target as Window).innerWidth < XXL_SCREEN);
        };

        window.addEventListener('resize', handleScreenSizeChange);

        return () =>
            window.removeEventListener('resize', handleScreenSizeChange);
    }, []);

    useZendeskWidget();
    usePageTitle();

    return (
        <>
            <WelcomeDialog />
            <div className="h-screen">
                <Navbar
                    isSidebarAvailable={isSidebarAvailable}
                    isSidebarLoading={isSidebarLoading}
                    isSidebarOpen={isSidebarOpen}
                    setIsSidebarOpen={setStoredIsSidebarOpen}
                />
                <div className="pt-navbar h-full">
                    <LoggedAsBanner />
                    <Sidebar
                        isExpanded={isSidebarOpen}
                        setIsSidebarOpen={setStoredIsSidebarOpen}
                    />
                    <main
                        className={classNames(
                            'flex flex-col h-full',
                            !isSidebarOpen ? 'ml-0' : 'xxl:ml-80'
                        )}
                        onMouseDown={() => {
                            isSmallScreen && setStoredIsSidebarOpen(false);
                        }}
                    >
                        <div className="flex-1">
                            <Outlet />
                        </div>
                        <div className="flex-none">
                            <Footer />
                        </div>

                        {isSidebarAvailable && (
                            <button
                                type="button"
                                data-testid="navigation-arrow-toggle"
                                className="fixed top-1/2 z-(--z-sidebar) cursor-pointer bg-primary-500 h-14 rounded-r p-1 hidden xxl:flex items-center"
                                onClick={() =>
                                    setStoredIsSidebarOpen(!isSidebarOpen)
                                }
                                aria-label={t(
                                    `layout:navigation-menu:${
                                        isSidebarOpen ? 'collapse' : 'expand'
                                    }`
                                )}
                            >
                                <SidebarToggleIcon
                                    size={16}
                                    aria-hidden="true"
                                />
                            </button>
                        )}
                    </main>
                </div>
            </div>
            <EnvironmentRibbon />
        </>
    );
};
export default Layout;
